<template>
  <div class="minimap">
    <div class="minimap-head">
      <span class="minimap-title">{{ title }}</span>
      <span class="minimap-axes">{{ axisLabel }}</span>
    </div>

    <div class="minimap-stage" :class="{ 'is-pannable': zoom > 1, 'is-panning': dragging }"
      @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="endPan" @pointercancel="endPan">
      <svg class="minimap-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <rect x="0" y="0" width="100" height="100" class="minimap-bg" />

        <!-- Footprint blocks (plan) or height reference lines (levels). -->
        <template v-if="mode !== 'levels'">
          <rect v-for="(c, i) in fillCells" :key="'c' + i" :x="c.x" :y="c.y" :width="c.s" :height="c.s"
            class="minimap-cell" />
        </template>
        <template v-else>
          <g v-for="(l, i) in placedLevels" :key="'l' + i">
            <line x1="6" :y1="l.y" x2="94" :y2="l.y" class="minimap-level" />
            <text x="7" :y="Number(l.y) - 1.4" class="minimap-level-label">{{ l.label }}</text>
          </g>
        </template>

        <!-- Camera walking path(s) — a thin line in capture order, one colour per scan, with
             start (ST) + finish (FN) dots so the capture direction reads at a glance. -->
        <polyline v-for="(pl, i) in placedPaths" :key="'p' + i" :points="pl.points"
          class="minimap-path" :style="{ stroke: pl.color }" />
        <g v-for="(e, i) in pathEndpoints" :key="'pe' + i">
          <circle :cx="e.x" :cy="e.y" r="1.3" class="minimap-path-end" :style="{ fill: e.color }" />
          <text :x="e.x" :y="e.y - 2.1" class="minimap-path-endlabel">{{ e.label }}</text>
        </g>

        <!-- Leader lines: a moved label tied back to its true position (anchor dot). -->
        <template v-for="p in placed" :key="'l' + p.id">
          <g v-if="p.visible && p.moved">
            <line :x1="p.labelLeft" :y1="p.labelTop" :x2="p.left" :y2="p.top" class="minimap-leader" />
            <circle :cx="p.left" :cy="p.top" r="0.9" class="minimap-anchor" :style="{ fill: p.color || '#64ffda' }" />
          </g>
        </template>
      </svg>

      <template v-for="p in placed" :key="p.id">
        <button v-if="p.visible" type="button" class="minimap-marker"
          :class="{ 'is-hovered': hoveredId === p.id, 'is-highlighted': highlightId === p.id }"
          :style="{ left: p.labelLeft + '%', top: p.labelTop + '%', '--dot': p.color || '#64ffda' }"
          :title="p.label || 'Anomaly'"
          @mouseenter="onEnter(p.id, $event)" @mouseleave="onLeave(p.id)"
          @click="$emit('select', p.id)">
          {{ p.number }}
        </button>
      </template>

      <!-- Teleported to <body> so the hover image is never clipped by the minimap/rail overflow;
           fixed-positioned above the hovered marker. -->
      <Teleport to="body">
        <div v-if="hovered && hovered.visible && tipAnchor" class="minimap-tip minimap-tip--fixed"
          :style="{ left: tipAnchor.x + 'px', top: tipAnchor.y + 'px' }">
          <div class="minimap-tip-imgwrap" :class="{ 'is-loading': tipLoading }">
            <img v-if="hovered.imageSrc" v-show="tipImgLoaded" :src="hovered.imageSrc" class="minimap-tip-img" alt=""
              @load="tipImgLoaded = true" @error="onTipImgError" />
            <img v-else-if="!hovered.hasImage" :src="noImageAvailable" class="minimap-tip-img" alt="No image available" />
          </div>
          <div class="minimap-tip-label">#{{ hovered.number }} · {{ hovered.label || 'Anomaly' }}</div>
          <div v-if="hovered.elevation" class="minimap-tip-elev" :class="'is-' + hovered.elevation.toLowerCase()">
            <span class="minimap-tip-elev-dot"></span>
            <span>Elevation: {{ hovered.elevation }}</span>
          </div>
        </div>
      </Teleport>

      <div v-if="!points.length" class="minimap-empty">No positioned anomalies</div>

      <!-- Zoom / fit controls -->
      <div class="minimap-controls" @pointerdown.stop>
        <button type="button" class="mm-btn" title="Fit to anomalies" @click="fitAnomalies">
          <v-icon size="15">mdi-image-filter-center-focus</v-icon>
        </button>
        <button type="button" class="mm-btn" title="Fit whole model" @click="resetView">
          <v-icon size="15">mdi-fit-to-page-outline</v-icon>
        </button>
        <button type="button" class="mm-btn" title="Zoom in" @click="zoomBy(1.4)">
          <v-icon size="16">mdi-plus</v-icon>
        </button>
        <button type="button" class="mm-btn" title="Zoom out" :disabled="zoom <= 1" @click="zoomBy(1 / 1.4)">
          <v-icon size="16">mdi-minus</v-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useDefaultImage } from '@/utils/ImageUtils';
import noImageAvailable from '@/assets/no-image-available.svg';
import { deCollideLabels } from '@/lib/viewer/minimapLabels';

export default {
  name: 'AnomalyMinimap',
  props: {
    title: { type: String, default: 'Minimap' },
    axisLabel: { type: String, default: '' },
    // 'plan' = footprint tile (uniform scale); 'levels' = height reference lines (stretch to fill).
    mode: { type: String, default: 'plan' },
    // Footprint slice points as [[u, v], ...] — rasterised into occupancy cells to show the shape.
    fill: { type: Array, default: () => [] },
    // Camera walking path(s): array of ordered polylines, each [[u, v], ...].
    paths: { type: Array, default: () => [] },
    // Horizontal reference lines (levels mode): [{ v, label }].
    levels: { type: Array, default: () => [] },
    // Force the framed extent (levels mode): { uMin, uMax, vMin, vMax }.
    frame: { type: Object, default: null },
    // Markers: [{ id, u, v, number, color, label, imageSrc }].
    points: { type: Array, default: () => [] },
    // Marker id to emphasise (e.g. the table row under the cursor).
    highlightId: { type: [String, Number], default: null },
  },
  emits: ['select', 'hover'],
  data() {
    // zoom + centre (centre is in base 0..100 space) form the view transform on top of baseMapper.
    return { hoveredId: null, tipImgLoaded: false, noImageAvailable, zoom: 1, centerU: 50, centerV: 50, dragging: false, panStart: null, tipAnchor: null };
  },
  computed: {
    // The framed extent. Plan mode frames to the CAPTURED WALK PATH (+ anomalies) when a path exists,
    // so stray cloud points far from where the operator actually walked don't blow the view out and
    // shrink everything into a corner. Falls back to the footprint extent when there's no path.
    bounds() {
      const us = [], vs = [];
      if (this.mode === 'levels') {
        if (this.frame) { us.push(this.frame.uMin, this.frame.uMax); vs.push(this.frame.vMin, this.frame.vMax); }
        for (const l of this.levels) vs.push(l.v);
        for (const line of this.paths) for (const pt of line.points) { us.push(pt[0]); vs.push(pt[1]); }
        for (const p of this.points) { us.push(p.u); vs.push(p.v); }
      } else if (this.paths.length) {
        for (const line of this.paths) for (const pt of line.points) { us.push(pt[0]); vs.push(pt[1]); }
        for (const p of this.points) { us.push(p.u); vs.push(p.v); }
      } else {
        for (const pt of this.fill) { us.push(pt[0]); vs.push(pt[1]); }
        for (const p of this.points) { us.push(p.u); vs.push(p.v); }
      }
      if (!us.length) return null;
      return { uMin: Math.min(...us), uMax: Math.max(...us), vMin: Math.min(...vs), vMax: Math.max(...vs) };
    },
    // Base map into a 0..100 box (v inverted so "up" is up). Plan mode keeps aspect (uniform + centred);
    // levels mode stretches to fill the tile (aspect isn't meaningful for a height schematic).
    baseMapper() {
      const b = this.bounds;
      if (!b) return null;
      const uR = Math.max(b.uMax - b.uMin, 1e-6);
      const vR = Math.max(b.vMax - b.vMin, 1e-6);
      const pad = 9;
      const usable = 100 - pad * 2;
      if (this.mode === 'levels') {
        return {
          fx: (u) => pad + ((u - b.uMin) / uR) * usable,
          fy: (v) => pad + ((b.vMax - v) / vR) * usable,
        };
      }
      const span = Math.max(uR, vR);
      const scale = usable / span;
      const offU = (usable - uR * scale) / 2;
      const offV = (usable - vR * scale) / 2;
      return {
        fx: (u) => pad + offU + (u - b.uMin) * scale,
        fy: (v) => pad + offV + (b.vMax - v) * scale,
      };
    },
    // The view transform (zoom about centre) composed over the base map — everything (SVG + markers)
    // reads through this, so the whole tile zooms/pans together.
    mapper() {
      const bm = this.baseMapper;
      if (!bm) return null;
      const z = this.zoom, cu = this.centerU, cv = this.centerV;
      return {
        fx: (u) => (bm.fx(u) - cu) * z + 50,
        fy: (v) => (bm.fy(v) - cv) * z + 50,
      };
    },
    placedLevels() {
      const m = this.mapper;
      if (!m) return [];
      return this.levels.map(l => ({ y: m.fy(l.v).toFixed(2), label: l.label }));
    },
    placed() {
      const m = this.mapper;
      if (!m) return [];
      const base = this.points.map(p => {
        const left = m.fx(p.u), top = m.fy(p.v);
        return { ...p, left, top, visible: left >= -2 && left <= 102 && top >= -2 && top <= 102 };
      });
      // De-collide only the on-screen labels; anchors (left/top) stay put, labels move to labelLeft/Top.
      const laid = deCollideLabels(
        base.filter(p => p.visible).map(p => ({ id: p.id, x: p.left, y: p.top })),
        { minSep: 7.5 },
      );
      const byId = new Map(laid.map(l => [l.id, l]));
      return base.map(p => {
        const l = byId.get(p.id);
        return {
          ...p,
          labelLeft: l ? l.lx : p.left,
          labelTop: l ? l.ly : p.top,
          moved: l ? l.moved : false,
        };
      });
    },
    // Rasterise the footprint points into occupancy cells (a low-res grid over the 0..100 tile).
    fillCells() {
      const m = this.mapper;
      if (!m || !this.fill.length) return [];
      const G = 52;
      const cell = 100 / G;
      const occ = new Set();
      for (const [u, v] of this.fill) {
        const cx = Math.floor(m.fx(u) / cell);
        const cy = Math.floor(m.fy(v) / cell);
        if (cx < 0 || cy < 0 || cx >= G || cy >= G) continue;
        occ.add(cx * G + cy);
      }
      const cells = [];
      for (const kkey of occ) {
        const cx = Math.floor(kkey / G), cy = kkey % G;
        cells.push({ x: (cx * cell).toFixed(2), y: (cy * cell).toFixed(2), s: (cell + 0.02).toFixed(2) });
      }
      return cells;
    },
    placedPaths() {
      const m = this.mapper;
      if (!m) return [];
      return this.paths
        .filter(line => line.points && line.points.length >= 2)
        .map(line => ({
          points: line.points.map(([u, v]) => `${m.fx(u).toFixed(2)},${m.fy(v).toFixed(2)}`).join(' '),
          color: line.color,
        }));
    },
    // Start (ST) + finish (FN) dots for each walking path, so the capture direction is readable.
    pathEndpoints() {
      const m = this.mapper;
      if (!m) return [];
      const out = [];
      for (const line of this.paths) {
        if (!line.points || line.points.length < 2) continue;
        const s = line.points[0], f = line.points[line.points.length - 1];
        out.push({ x: m.fx(s[0]), y: m.fy(s[1]), color: line.color, label: 'ST' });
        out.push({ x: m.fx(f[0]), y: m.fy(f[1]), color: line.color, label: 'FN' });
      }
      return out;
    },
    hovered() {
      return this.placed.find(p => p.id === this.hoveredId) || null;
    },
    // Shimmer while the tooltip image is being signed (imageSrc not yet set) or still decoding.
    tipLoading() {
      const h = this.hovered;
      if (!h) return false;
      if (h.hasImage && !h.imageSrc) return true;
      if (h.imageSrc && !this.tipImgLoaded) return true;
      return false;
    },
  },
  watch: {
    hoveredId() { this.tipImgLoaded = false; },
  },
  methods: {
    useDefaultImage,
    onTipImgError(e) {
      useDefaultImage(e);
      this.tipImgLoaded = true;   // stop shimmering even if the thumbnail failed
    },
    onEnter(id, event) {
      this.hoveredId = id;
      // Capture the marker's on-screen position so the tooltip (teleported to <body>) can sit above
      // it with fixed positioning — escaping the minimap/rail overflow that would otherwise clip it.
      const el = event?.currentTarget;
      if (el && el.getBoundingClientRect) {
        const r = el.getBoundingClientRect();
        // The tooltip is 190px wide and centred on this x (translateX(-50%)); clamp so it can't run off
        // either viewport edge.
        const half = 99;   // half the tip width + a small margin
        const cx = r.left + r.width / 2;
        const x = Math.min(Math.max(cx, half), window.innerWidth - half);
        this.tipAnchor = { x, y: r.top };
      }
      this.$emit('hover', id);   // let the sibling minimap highlight the same anomaly
    },
    onLeave(id) {
      if (this.hoveredId === id) {
        this.hoveredId = null;
        this.tipAnchor = null;
        this.$emit('hover', null);
      }
    },
    // Zoom the view so the anomalies fill the tile (with padding). Clamped so a single marker doesn't
    // zoom to infinity. Centre + zoom are in the base 0..100 space that both SVG and markers read.
    fitAnomalies() {
      const bm = this.baseMapper;
      if (!bm || !this.points.length) return;
      const us = this.points.map(p => bm.fx(p.u));
      const vs = this.points.map(p => bm.fy(p.v));
      let uMin = Math.min(...us), uMax = Math.max(...us), vMin = Math.min(...vs), vMax = Math.max(...vs);
      const padU = Math.max((uMax - uMin) * 0.25, 8), padV = Math.max((vMax - vMin) * 0.25, 8);
      uMin -= padU; uMax += padU; vMin -= padV; vMax += padV;
      const w = Math.max(uMax - uMin, 1), h = Math.max(vMax - vMin, 1);
      this.centerU = (uMin + uMax) / 2;
      this.centerV = (vMin + vMax) / 2;
      this.zoom = Math.min(Math.max(Math.min(100 / w, 100 / h), 1), 8);
    },
    resetView() {
      this.zoom = 1;
      this.centerU = 50;
      this.centerV = 50;
    },
    zoomBy(factor) {
      const z = Math.min(Math.max(this.zoom * factor, 1), 8);
      this.zoom = z;
      if (z === 1) { this.centerU = 50; this.centerV = 50; }
    },
    onPointerDown(e) {
      if (this.zoom <= 1) return;
      if (e.target.closest('.minimap-marker') || e.target.closest('.minimap-controls')) return;
      const r = e.currentTarget.getBoundingClientRect();
      this.dragging = true;
      this.panStart = { x: e.clientX, y: e.clientY, cu: this.centerU, cv: this.centerV, w: r.width, h: r.height };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove(e) {
      if (!this.dragging || !this.panStart) return;
      const dx = e.clientX - this.panStart.x, dy = e.clientY - this.panStart.y;
      this.centerU = this.panStart.cu - (dx / this.panStart.w) * 100 / this.zoom;
      this.centerV = this.panStart.cv - (dy / this.panStart.h) * 100 / this.zoom;
    },
    endPan() {
      this.dragging = false;
      this.panStart = null;
    },
  },
};
</script>

<style scoped>
.minimap {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  padding: 12px 12px 14px;
}

.minimap-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.minimap-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.minimap-axes {
  font-size: 0.68rem;
  color: #5f727a;
  font-variant-numeric: tabular-nums;
}

.minimap-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  /* Not clipped, so a marker's hover image can pop out above the tile without being cut off. */
  overflow: visible;
  touch-action: none;
}

.minimap-stage.is-pannable {
  cursor: grab;
}

.minimap-stage.is-panning {
  cursor: grabbing;
}

.minimap-controls {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 4;
}

.mm-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(10, 18, 22, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mm-btn:hover:not(:disabled) {
  background: rgba(100, 255, 218, 0.14);
  border-color: rgba(100, 255, 218, 0.35);
  color: #fff;
}

.mm-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.minimap-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.minimap-bg {
  fill: rgba(0, 0, 0, 0.22);
}

.minimap-cell {
  fill: rgba(120, 200, 190, 0.42);
}

.minimap-path {
  fill: none;
  stroke: #ffc65c;
  stroke-width: 0.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.minimap-path-end {
  stroke: #12201f;
  stroke-width: 0.4;
}

.minimap-path-endlabel {
  fill: #cfd8dc;
  font-size: 3px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-anchor: middle;
  paint-order: stroke;
  stroke: #12201f;
  stroke-width: 0.6;
}

/* Leader line from a de-collided label back to its true position + a small anchor dot there. */
.minimap-leader {
  stroke: rgba(255, 255, 255, 0.45);
  stroke-width: 0.4;
}

.minimap-anchor {
  stroke: #12201f;
  stroke-width: 0.3;
}

.minimap-level {
  stroke: rgba(120, 200, 190, 0.45);
  stroke-width: 0.4;
  stroke-dasharray: 2 1.5;
}

.minimap-level-label {
  fill: #7f95a0;
  font-size: 3px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.minimap-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #12201f;
  border: 2px solid var(--dot);
  color: #fff;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  z-index: 1;
}

.minimap-marker:hover,
.minimap-marker.is-hovered {
  transform: translate(-50%, -50%) scale(1.25);
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.2);
  z-index: 3;
}

/* Emphasised when its table row is hovered — larger + ring so you can spot it on both maps. */
.minimap-marker.is-highlighted {
  transform: translate(-50%, -50%) scale(1.7);
  border-color: #fff;
  box-shadow: 0 0 0 4px rgba(255, 198, 92, 0.55);
  z-index: 4;
}

.minimap-tip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 12px));
  z-index: 10000;
  width: 190px;
}

/* Teleported-to-body variant: fixed to the viewport so no ancestor overflow can clip it. */
.minimap-tip.minimap-tip--fixed {
  position: fixed;
  padding: 6px;
  background: #0f171b;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

.minimap-tip-imgwrap {
  position: relative;
  width: 100%;
  height: 160px;
  background: #1c272c;
  border-radius: 6px;
  overflow: hidden;
}
.minimap-tip-imgwrap.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: mm-tip-shimmer 1.1s ease-in-out infinite;
}
@keyframes mm-tip-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) {
  .minimap-tip-imgwrap.is-loading::after { animation: none; }
}

.minimap-tip-img {
  width: 100%;
  height: 160px;
  object-fit: contain;
  background: #1c272c;
  border-radius: 6px;
  display: block;
}

.minimap-tip-label {
  margin-top: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #cfd8dc;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Height band (replaces the removed side-elevation minimap): Low / Medium / High with a colour cue. */
.minimap-tip-elev {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #9fb2bb;
}
.minimap-tip-elev-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: #8aa0aa;
}
.minimap-tip-elev.is-high .minimap-tip-elev-dot { background: #64ffda; }
.minimap-tip-elev.is-medium .minimap-tip-elev-dot { background: #5eb0d6; }
.minimap-tip-elev.is-low .minimap-tip-elev-dot { background: #7aa2ff; }

.minimap-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f727a;
  font-size: 0.8rem;
  text-align: center;
  padding: 0 12px;
}
</style>
